import { computed, state } from "@mhmo91/schmancy/state";
import { Workspace } from "@momo/types";
import { Subject } from "rxjs";
import { signedIn } from "../auth/session.state.ts";
import { invitationsOnScreen, invitationsRead } from "./invitation.state.ts";

// The organization this document reads under; null where none resolved.
// Bound to the document, not to whoever is signed in.
export const organizationId = state<string | null>(
	"workspace/organization-id",
).memory(null, { keepOnClear: true });

// The address that selected this organization: an explicit native path,
// the native host's configured public default, or a claimed host.
export const organizationAddress = state<"path" | "default" | "host">(
	"workspace/organization-address",
).memory("path", { keepOnClear: true });

// The deployment configuration remains available while another organization
// or a neutral sign-in address is open.
export const defaultOrganizationId = state<string | null>(
	"workspace/default-organization-id",
).memory(null, { keepOnClear: true });

export function organizationAtPath(
	pathname: string,
	defaultId: string | null,
): string | null {
	const segment = /^\/o\/([^/]+)(?:\/|$)/.exec(pathname)?.[1];
	if (segment) {
		try {
			const parsed = Workspace.OrganizationIdSchema.safeParse(
				decodeURIComponent(segment),
			);
			return parsed.success ? parsed.data : null;
		} catch {
			return null;
		}
	}
	if (/^\/(?:o|admin|sign-in)(?:\/|$)/.test(pathname)) return null;
	return defaultId;
}

export const organizationState = state<{
	status: "loading" | "ready" | "error";
	// The events this person holds a grant on in the open organization; `null`
	// until those grants have answered, so no gate decides from a read that
	// has not happened.
	eventIds: readonly string[] | null;
	organizations: readonly { id: string; brand: Workspace.Workspace["brand"] }[];
	switching: boolean;
	error: string;
}>("workspace/organizations").memory(
	{
		status: "loading",
		eventIds: null,
		organizations: [],
		switching: false,
		error: "",
	},
	{ keepOnClear: true },
);
export const selectOrganization$ = new Subject<string>();
// The saved selection must answer before the bare host can reveal a chooser.
export const organizationSelectionRead = state<"loading" | "ready" | "error">(
	"workspace/organization-selection-read",
).memory("loading");
export const organizationSelection = computed(() => ({
	...organizationState.value,
	organizationId: organizationId.value,
}));

export const organizationPathPrefix = computed(() =>
	organizationId.value && organizationAddress.value === "path"
		? `/o/${encodeURIComponent(organizationId.value)}`
		: "",
);

// What the native host shows when no organization resolved: the person's own
// organizations and open invitations, never every organization in the
// project. Both reads settle the arm: `empty` — founding one — is reached
// only once each has answered with nothing, so no invitee is sent to found
// an organization while their invitation is still being read, and a failed
// invitations read is its own arm only when there is no organization to
// choose beside it.
export const organizationEntry = computed(() =>
	signedIn.value === null ||
	(signedIn.value &&
		(organizationState.value.status === "loading" ||
			invitationsRead.value === "loading"))
		? ("reading" as const)
		: !signedIn.value
			? ("signed-out" as const)
			: organizationState.value.status === "error"
				? ("error" as const)
				: invitationsRead.value === "error" &&
						organizationState.value.organizations.length === 0
					? ("invitations-error" as const)
					: organizationState.value.organizations.length +
								invitationsOnScreen.get().length ===
							0
						? ("empty" as const)
						: ("choose" as const),
);

// The organization the native host opens for a signed-in person: the saved one
// while a grant still covers it, else their only one. A saved value this tab
// has not yet committed decides nothing, since the write may still be refused.
export function organizationToOpen(
	saved: { readonly workspaceId: string | null; readonly pending: boolean },
	held: readonly string[],
): string | null {
	if (saved.pending) return null;
	if (saved.workspaceId && held.includes(saved.workspaceId))
		return saved.workspaceId;
	return held.length === 1 ? (held[0] ?? null) : null;
}
